/**
 * React Query hooks for lead management
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { leadService } from "../Lead.service";
import type {
  Lead,
  LeadStatus,
  LeadNote,
  LeadFilterParams,
  CreateLeadData,
  UpdateLeadData,
  LogContactData,
  CSVImportResult,
  BulkUpdateData,
  PlaceResult,
} from "../Lead.types";

// Query keys
export const leadKeys = {
  all: ["leads"] as const,
  lists: () => [...leadKeys.all, "list"] as const,
  list: (filters: { includeConverted?: boolean; status?: string }) =>
    [...leadKeys.lists(), filters] as const,
  filtered: (params: LeadFilterParams) => [...leadKeys.lists(), "filtered", params] as const,
  details: () => [...leadKeys.all, "detail"] as const,
  detail: (id: string) => [...leadKeys.details(), id] as const,
  notes: (leadId: string) => [...leadKeys.all, "notes", leadId] as const,
  tags: () => [...leadKeys.all, "tags"] as const,
};

/**
 * Hook to fetch all leads
 */
export function useLeads(includeConverted: boolean = false) {
  return useQuery({
    queryKey: leadKeys.list({ includeConverted }),
    queryFn: () => leadService.getLeads(includeConverted),
  });
}

/**
 * Hook to fetch the DB-owned lead tag catalog (TD-041). Rarely changes, so it
 * is cached long and shared by the table (label resolution) and modal (picker).
 */
export function useLeadTags() {
  return useQuery({
    queryKey: leadKeys.tags(),
    queryFn: () => leadService.getLeadTags(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch leads by status
 */
export function useLeadsByStatus(status: LeadStatus) {
  return useQuery({
    queryKey: leadKeys.list({ status }),
    queryFn: () => leadService.getLeadsByStatus(status),
  });
}

/**
 * Hook to fetch a single lead
 */
export function useLead(leadId: string | null) {
  return useQuery({
    queryKey: leadKeys.detail(leadId || ""),
    queryFn: () => leadService.getLead(leadId!),
    enabled: !!leadId,
  });
}

/**
 * Hook to create a new lead
 */
export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLeadData) => leadService.createLead(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}

/**
 * Hook to update a lead
 */
export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, data }: { leadId: string; data: UpdateLeadData }) =>
      leadService.updateLead(leadId, data),
    onSuccess: (updatedLead: Lead) => {
      queryClient.setQueryData(leadKeys.detail(updatedLead.id), updatedLead);
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}

/**
 * Hook to delete a lead
 */
export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: string) => leadService.deleteLead(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}

/**
 * Hook to log a contact attempt
 */
export function useLogContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, data }: { leadId: string; data: LogContactData }) =>
      leadService.logContact(leadId, data),
    onSuccess: (updatedLead: Lead) => {
      queryClient.setQueryData(leadKeys.detail(updatedLead.id), updatedLead);
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}

/**
 * Hook to update lead status
 */
export function useUpdateLeadStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, status }: { leadId: string; status: LeadStatus }) =>
      leadService.updateStatus(leadId, status),
    onSuccess: (updatedLead: Lead) => {
      queryClient.setQueryData(leadKeys.detail(updatedLead.id), updatedLead);
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}

/**
 * Hook to convert a lead to a client
 */
export function useConvertLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: string) => leadService.convertToClient(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      // Also invalidate clients list since a new client was created
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

/**
 * Hook to import leads from CSV
 */
export function useImportLeadsCSV() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      fieldMapping,
      skipDuplicates,
      city,
    }: {
      file: File;
      fieldMapping?: Record<string, string>;
      skipDuplicates?: boolean;
      city?: string;
    }) => leadService.importCSV(file, { fieldMapping, skipDuplicates, city }),
    onSuccess: (result: CSVImportResult) => {
      if (result.created > 0) {
        queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      }
    },
  });
}

/**
 * Hook to preview CSV import
 */
export function usePreviewCSV() {
  return useMutation({
    mutationFn: ({
      file,
      fieldMapping,
    }: {
      file: File;
      fieldMapping?: Record<string, string>;
    }) => leadService.previewCSV(file, fieldMapping),
  });
}

/**
 * Hook to fetch leads with filtering and sorting
 */
export function useLeadsFiltered(params: LeadFilterParams = {}) {
  return useQuery({
    queryKey: leadKeys.filtered(params),
    queryFn: () => leadService.getLeadsFiltered(params),
  });
}

/**
 * Hook to fetch notes for a lead
 */
export function useLeadNotes(leadId: string | null) {
  return useQuery({
    queryKey: leadKeys.notes(leadId || ""),
    queryFn: () => leadService.getLeadNotes(leadId!),
    enabled: !!leadId,
  });
}

/**
 * Hook to add a note to a lead
 */
export function useAddLeadNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      leadId,
      content,
      status,
    }: {
      leadId: string;
      content: string;
      status?: string;
    }) => leadService.addLeadNote(leadId, content, status),
    onSuccess: (newNote: LeadNote) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.notes(newNote.leadId) });
    },
  });
}

/**
 * Hook to delete a lead note
 */
export function useDeleteLeadNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, noteId }: { leadId: string; noteId: string }) =>
      leadService.deleteLeadNote(leadId, noteId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.notes(variables.leadId) });
    },
  });
}

/**
 * Hook to bulk update multiple leads
 */
export function useBulkUpdateLeads() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadIds, updates }: { leadIds: string[]; updates: BulkUpdateData }) =>
      leadService.bulkUpdate(leadIds, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}

/**
 * Hook to bulk delete multiple leads
 */
export function useBulkDeleteLeads() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadIds: string[]) => leadService.bulkDelete(leadIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}

/**
 * Hook to search Google Places
 */
export function useSearchPlaces() {
  return useMutation({
    mutationFn: ({
      category,
      city,
      country,
      useCache,
    }: {
      category: string;
      city: string;
      country?: string;
      useCache?: boolean;
    }) => leadService.searchPlaces(category, city, country, useCache),
  });
}

/**
 * Hook to import places as leads
 */
export function useImportPlaces() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (places: PlaceResult[]) => leadService.importPlaces(places),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}
